import { Fragment, useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  Tabs,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useDisclaimer from '../../hooks/useDisclaimer';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import DoneIcon from '@mui/icons-material/Done';
import PersonIcon from '@mui/icons-material/Person';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import WaiverList from '../Waivers/WaiverList';
import WaiverSignatureModal from '../Waivers/WaiverSignatureModal';

const REFRESH_INTERVAL_MS = 60 * 1000;

// The backend groups signatures whose lane nobody picked under laneNum null (the
// external form leaves its lane field on a placeholder). They keep their own
// column instead of being labelled "Bahn null".
function laneLabel(laneNum) {
  return laneNum === null || laneNum === undefined || laneNum === ''
    ? 'Ohne Bahn'
    : 'Bahn ' + laneNum;
}

function LaneList(props) {
  const { laneData } = props;
  const [selectedWaiver, setSelectedWaiver] = useState(null);

  return (
    <>
      <Paper sx={{ margin: '0 10px' }}>
        <List sx={{ width: '300px' }}>
          <ListItem>
            <ListItemText
              primary={laneLabel(laneData.laneNum)}
              primaryTypographyProps={{
                fontWeight: 'bold',
                fontSize: '1.2em',
                textAlign: 'center',
              }}
            ></ListItemText>
          </ListItem>
          <Divider />
          {laneData.data.map(person => (
            <Fragment key={person._id}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => setSelectedWaiver(person)}>
                  <ListItemText
                    primary={person.lastName + ', ' + person.firstName}
                    secondary={
                      !!person.children.length &&
                      '+ ' + person.children.map(c => c.firstName).join(', ')
                    }
                    primaryTypographyProps={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  ></ListItemText>
                </ListItemButton>
              </ListItem>
              <Divider />
            </Fragment>
          ))}
        </List>
      </Paper>
      <WaiverSignatureModal
        waiver={selectedWaiver}
        open={!!selectedWaiver}
        onClose={() => setSelectedWaiver(null)}
      />
    </>
  );
}

// Waivers signed at the venue within the last hour, grouped by lane.
function CurrentLaneWaivers() {
  const [data, setData] = useState([]);
  const { getUnseenDisclaimers } = useDisclaimer();

  const fetchData = useCallback(async () => {
    const disclaimers = await getUnseenDisclaimers();
    setData(disclaimers || []);
  }, [getUnseenDisclaimers]);

  useEffect(() => {
    fetchData().catch(e => {
      toast.error('Fehler beim Laden');
      console.error(e);
    });

    const interval = setInterval(() => {
      fetchData().catch(e => {
        console.error(e);
        toast.error('Fehler beim Laden');
      });
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  if (!data.length) {
    return (
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='center'
        sx={{ width: '100%', marginTop: '50px' }}
      >
        <HourglassTopRoundedIcon sx={{ fontSize: '100', marginRight: '10px' }} />
        <Typography variant='h4' component='div' align='center'>
          Warte auf neue Daten...
        </Typography>
      </Stack>
    );
  }

  return (
    <>
      <Tabs variant='scrollable' scrollButtons={false} value={false}>
        {data.map(lane => (
          <LaneList laneData={lane} key={lane.laneNum ?? 'ohne-bahn'} />
        ))}
      </Tabs>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='center'
        sx={{ width: '100%', marginTop: '50px' }}
      >
        <KeyboardDoubleArrowLeftIcon sx={{ marginRight: '10px' }} />
        <Typography variant='body1' component='div' align='center'>
          Scrollen um alle Bahnen zu sehen
        </Typography>
        <KeyboardDoubleArrowRightIcon sx={{ marginLeft: '10px' }} />
      </Stack>
    </>
  );
}

// Waivers customers filled in before their visit, grouped by booking. These never
// appear in the lane columns because they carry no lane.
function AdvanceWaivers() {
  const { getAdvanceDisclaimers } = useDisclaimer();
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [bookings, setBookings] = useState([]);
  // Advance waivers that belong to no booking. Without this list they would be
  // invisible everywhere although the guest submitted them successfully.
  const [unassigned, setUnassigned] = useState([]);
  const [unassignedTruncated, setUnassignedTruncated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isoDate = selectedDate.format('YYYY-MM-DD');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAdvanceDisclaimers(isoDate);
      setBookings(response?.bookings || []);
      setUnassigned(response?.unassignedWaivers || []);
      setUnassignedTruncated(!!response?.unassignedTruncated);
    } finally {
      setIsLoading(false);
    }
  }, [getAdvanceDisclaimers, isoDate]);

  useEffect(() => {
    fetchData().catch(e => {
      console.error(e);
      toast.error('Fehler beim Laden der vorab ausgefüllten Waiver');
    });

    const interval = setInterval(() => {
      fetchData().catch(e => {
        console.error(e);
      });
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isoDate]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <DatePicker
          label='Datum'
          value={selectedDate}
          onChange={value => value && setSelectedDate(value)}
          slotProps={{ textField: { size: 'small' } }}
          sx={{ width: { xs: '100%', sm: 200 } }}
        />
        <Button
          variant='outlined'
          onClick={() =>
            fetchData().catch(e => {
              console.error(e);
              toast.error('Fehler beim Laden der vorab ausgefüllten Waiver');
            })
          }
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={22} /> : 'Aktualisieren'}
        </Button>
      </Stack>

      {!bookings.length && !isLoading && (
        <Typography variant='body1' color='text.secondary'>
          Für {selectedDate.format('DD.MM.YYYY')} gibt es keine Buchungen mit Haftungserklärung.
        </Typography>
      )}

      {bookings.map(booking => {
        const missingCount = Math.max(
          0,
          (booking.participantCount || 0) - (booking.signedPersonCount || 0)
        );
        const allSigned = (booking.participantCount || 0) > 0 && missingCount === 0;

        return (
          <Accordion key={booking.bookingId} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                flexWrap='wrap'
                sx={{ width: '100%', pr: 1 }}
              >
                <Typography variant='body1' sx={{ fontWeight: 600, minWidth: '60px' }}>
                  {dayjs(booking.startTime).format('HH:mm')}
                </Typography>
                <Typography variant='body1'>
                  {booking.lastName}, {booking.firstName}
                </Typography>
                <Chip label={booking.bookingNumber} size='small' variant='outlined' />
                <Box sx={{ flex: 1 }} />
                <Chip
                  color={allSigned ? 'success' : booking.signedPersonCount > 0 ? 'warning' : 'default'}
                  icon={allSigned ? <DoneIcon /> : <PersonIcon />}
                  label={`${booking.signedPersonCount} von ${booking.participantCount} unterschrieben`}
                  size='small'
                />
                {booking.advanceSignedPersonCount > 0 && (
                  <Chip
                    color='success'
                    variant='outlined'
                    label={`${booking.advanceSignedPersonCount} vorab`}
                    size='small'
                  />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {booking.waivers?.length ? (
                <WaiverList waivers={booking.waivers} dense />
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  Noch keine Haftungserklärung eingegangen.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {!!unassigned.length && (
        <Paper variant='outlined' sx={{ mt: 3, borderColor: 'warning.main' }}>
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            flexWrap='wrap'
            sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Typography variant='body1' sx={{ fontWeight: 600 }}>
              Nicht zugeordnet
            </Typography>
            <Chip label={unassigned.length} size='small' color='warning' />
            <Typography variant='body2' color='text.secondary'>
              Vorab unterschrieben, aber ohne Buchungsnummer abgegeben — bitte anhand von Name und
              E-Mail der passenden Buchung zuordnen.
            </Typography>
          </Stack>
          <Box sx={{ px: 2, py: 1 }}>
            <WaiverList waivers={unassigned} dense />
          </Box>
          {unassignedTruncated && (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ px: 2, pb: 2, borderTop: 1, borderColor: 'divider', pt: 1 }}
            >
              Es gibt weitere nicht zugeordnete Haftungserklärungen als hier angezeigt werden.
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default function Waivers() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box sx={viewRootTextPrimarySx}>
      <Tabs
        value={activeTab}
        onChange={(event, value) => setActiveTab(value)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label='Aktuell an den Bahnen' />
        <Tab label='Vorab ausgefüllt' />
      </Tabs>
      {activeTab === 0 ? <CurrentLaneWaivers /> : <AdvanceWaivers />}
    </Box>
  );
}
